import { listarVariaveisObservacao, selecionarFormulasPorCriterio } from '@shared/components/formula/utils/formula-contexto.util';
import { coletarEstadosFormulas } from '@shared/components/formula/utils/formula-estados.util';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmModalComponent } from '@shared/components/confirm-modal/confirm-modal.component';
import { AtivacaoSectionCardComponent } from '@shared/components/ativacao-section-card/ativacao-section-card.component';
import { PisTributoObservacoesSectionComponent } from './sections/tributo-observacoes-section/tributo-observacoes-section.component';
import { PisTributoObservacoesFiscoSectionComponent } from './sections/tributo-observacoes-fisco-section/tributo-observacoes-fisco-section.component';
import { MAPA_CAMPOS_PIS_NATUREZA, PisTributoRegraSectionComponent } from './sections/tributo-regra-section/tributo-regra-section.component';
import { PisTributoExcecoesSectionComponent } from './sections/tributo-excecoes-section/tributo-excecoes-section.component';
import { FormulaEditorDialogComponent, FormulaEditorDialogData, FormulaEditorDialogResult } from '@shared/components/formula/formula-editor-dialog/formula-editor-dialog.component';
import { VariaveisDisponiveisDialogComponent, VariaveisDisponiveisDialogData, VariaveisCursorPayload } from '@shared/components/formula/variaveis-disponiveis-dialog/variaveis-disponiveis-dialog.component';
import { NaturezaOperacaoPisExcecaoPendenteStruct } from '@shared/services/structs/motor-fiscal/natureza-operacao-pis-excecao-pendente.struct';
import { FormulaItemStruct } from '@shared/services/structs/formula/formula-item.struct';
import { CstPisStruct } from '@shared/services/structs/motor-fiscal/cst-pis.struct';
import { ModalidadeBcOption } from '@shared/services/structs/motor-fiscal/modalidade-bc-option.struct';
import { TipoFormulaEnum } from '@shared/enums/formula/tipo-formula.enum';
import { TipoOperacaoEnum } from '@shared/enums/motor-fiscal/tipo-operacao.enum';
import { planejarTrocaCodigoFormulas } from '@shared/components/formula/utils/troca-codigo-formulas.util';
import { FormulaCatalogoItemStruct } from '@shared/services/structs/formula/formula-catalogo-item.struct';
import { FormulaCatalogoService } from '@shared/services/apis/motor-fiscal/formula-catalogo.service';
import { VariavelStruct } from '@shared/services/structs/formula/variavel.struct';
import {
    aplicarObrigatoriedadeIndicadores,
    controlesDasRegrasEntreCampos,
    lerIndicadoresCst,
} from '@shared/constants/motor-fiscal/indicadores-campos.util';

@Component({
    selector: 'app-natureza-operacao-pis-tab',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        AtivacaoSectionCardComponent,
        PisTributoRegraSectionComponent,
        PisTributoObservacoesSectionComponent,
        PisTributoObservacoesFiscoSectionComponent,
        PisTributoExcecoesSectionComponent,
    ],
    templateUrl: './natureza-operacao-pis-tab.component.html',
    styleUrl: './natureza-operacao-pis-tab.component.css',
})
export class NaturezaOperacaoPisTabComponent implements OnInit {
    @Input({ required: true }) form!: FormGroup;
    @Input() codigoTributos: CstPisStruct[] = [];
    @Input() modalidadesBcSt: ModalidadeBcOption[] = [];
    @Input() formulasPis: FormulaCatalogoItemStruct[] = [];
    @Input() estadosFormulas: FormulaItemStruct[] = [];
    @Input() idNaturezaOperacao: number | null = null;
    @Input() excecoesPendentes: NaturezaOperacaoPisExcecaoPendenteStruct[] = [];
    @Output() excecoesChange = new EventEmitter<NaturezaOperacaoPisExcecaoPendenteStruct[]>();

    private codigoTributoAnterior: string | null = null;
    private restaurandoCodigoTributo = false;

    ngOnInit(): void {
        this.isAtivoControl.valueChanges.subscribe(() => this.sincronizarObrigatoriedade());
        this.codigoTributoAnterior = this.cstPisValue;
        this.pisForm.get('cstPis')?.valueChanges.subscribe(codigo => {
            this.sincronizarObrigatoriedade();
            this.confirmarTrocaCodigoTributo(codigo);
        });
        controlesDasRegrasEntreCampos(MAPA_CAMPOS_PIS_NATUREZA).forEach(nome =>
            this.pisForm.get(nome)?.valueChanges.subscribe(() => this.sincronizarObrigatoriedade()));
        this.sincronizarObrigatoriedade();
    }

    /** CST exigido com o tributo ligado; os campos do código, pelo indicador do catálogo. */
    private sincronizarObrigatoriedade(): void {
        const ativo = !!this.isAtivoControl.value;
        const codigo = this.pisForm.get('cstPis')?.value;
        this.aplicarObrigatorio(this.pisForm.get('cstPis'), ativo);
        const indicadores = ativo && codigo ? lerIndicadoresCst(this.codigoTributos.find(c => c.codigo === codigo)?.indicadores) : null;
        aplicarObrigatoriedadeIndicadores(this.pisForm, indicadores, MAPA_CAMPOS_PIS_NATUREZA);
    }

    private aplicarObrigatorio(control: import('@angular/forms').AbstractControl | null | undefined, obrigatorio: boolean): void {
        if (!control) return;
        if (obrigatorio) {
            control.addValidators(Validators.required);
        } else {
            control.removeValidators(Validators.required);
        }
        control.updateValueAndValidity({ emitEvent: false });
    }

    public get pisForm(): FormGroup {
        return this.form.get('pis') as FormGroup;
    }

    public get isAtivoControl(): FormControl {
        return this.pisForm.get('isAtivo') as FormControl;
    }

    public get cstPisValue(): string | null {
        return this.pisForm?.get('cstPis')?.value ?? null;
    }

    /** Tipo de operação da natureza (Entrada/Saída), lido do form raiz. */
    public get tipoOperacaoValue(): TipoOperacaoEnum {
        return this.form?.get('tipoOperacao')?.value ?? TipoOperacaoEnum.Saida;
    }

    public get formulasParaCodigoTributo(): FormulaCatalogoItemStruct[] {
        return this.obterFormulasParaCodigo(this.cstPisValue);
    }

    private obterFormulasParaCodigo(codigo: string | null): FormulaCatalogoItemStruct[] {
        if (!codigo) return this.formulasPis;
        return selecionarFormulasPorCriterio(this.formulasPis, 'cst_pis', codigo);
    }

    /** Fonte única em memória das fórmulas: o FormGroup, que o salvar() também lê.
        O @Input estadosFormulas serve só de seed inicial (o pai já popula o control). */
    private get estadosFormulasMemoria(): FormulaItemStruct[] {
        return (this.pisForm.get('_estadosFormulas')?.value as FormulaItemStruct[]) ?? [];
    }

    public get personalizadasCount(): number {
        return this.estadosFormulasMemoria.filter(e => e.tipo === TipoFormulaEnum.Personalizada).length;
    }

    /** Variáveis únicas (por tag) disponíveis nas fórmulas do SN. */
    public get variaveisUnicas(): VariavelStruct[] {
        return listarVariaveisObservacao(this.formulasParaCodigoTributo);
    }

    constructor(
        private dialog: MatDialog,
        private formulaCatalogoService: FormulaCatalogoService,
    ) {}

    private confirmarTrocaCodigoTributo(novoCodigo: string | null): void {
        if (this.restaurandoCodigoTributo || novoCodigo === this.codigoTributoAnterior) return;
        const plano = planejarTrocaCodigoFormulas(this.estadosFormulasMemoria,
            this.obterFormulasParaCodigo(this.codigoTributoAnterior), this.obterFormulasParaCodigo(novoCodigo), {
                observacao: this.pisForm.get('observacao')?.value,
                observacaoFisco: this.pisForm.get('observacaoFisco')?.value,
            });
        if (plano.estadosParaRestaurar.length === 0 && plano.variaveisPendentes.length === 0) {
            this.codigoTributoAnterior = novoCodigo;
            return;
        }

        const partes = [
            plano.estadosParaRestaurar.length > 0 ? `restaurará ${plano.estadosParaRestaurar.length} configuraç${plano.estadosParaRestaurar.length === 1 ? 'ão' : 'ões'} de fórmulas` : null,
            plano.variaveisPendentes.length > 0 ? `deixará pendentes as variáveis [${plano.variaveisPendentes.map(nome => `{${nome}}`).join(', ')}] nas observações` : null,
        ].filter(Boolean).join(' e ');
        this.dialog.open(ConfirmModalComponent, {
            data: { title: 'Confirmar troca de CST', description: `A troca para CST ${novoCodigo ?? 'não informado'} ${partes}. Deseja continuar?`, buttonConfirm: 'Continuar', buttonClose: 'Cancelar' },
        }).afterClosed().subscribe(result => {
            if (!result?.confirm) {
                this.restaurandoCodigoTributo = true;
                this.pisForm.get('cstPis')?.setValue(this.codigoTributoAnterior, { emitEvent: false });
                this.restaurandoCodigoTributo = false;
                this.sincronizarObrigatoriedade();
                return;
            }
            this.pisForm.get('_estadosFormulas')?.setValue(this.estadosFormulasMemoria.filter(estado => !plano.estadosParaRestaurar.includes(estado)));
            Object.entries(plano.observacoes).forEach(([campo, valor]) => this.pisForm.get(campo)?.setValue(valor));
            this.codigoTributoAnterior = novoCodigo;
        });
    }

    public abrirEditorFormulas(): void {
        const codigoTributo = this.codigoTributos.find(c => c.codigo === this.cstPisValue);
        const data: FormulaEditorDialogData = {
            titulo: ['PIS', this.cstPisValue, codigoTributo?.descricao].filter(Boolean).join(' - '),
            formulas: this.formulasParaCodigoTributo.map(formula => ({
                ...formula,
                variaveis: (formula.variaveisPermitidas ?? formula.variaveis),
            })),
            estadosIniciais: this.estadosFormulasMemoria,
            estadosFormulasAgregados: coletarEstadosFormulas(this.form),
            contextoRaiz: 'natureza-operacao',
            validarPersonalizacoes: request => this.formulaCatalogoService.validarPersonalizacoes(request),
        };

        const ref = this.dialog.open(FormulaEditorDialogComponent, {
            width: '900px',
            maxWidth: '96vw',
            maxHeight: '92vh',
            data,
        });

        ref.afterClosed().subscribe((result: FormulaEditorDialogResult | null) => {
            if (result) {
                this.pisForm.get('_estadosFormulas')?.setValue(result.formulas);
            }
        });
    }

    public abrirVariaveisDisponiveis(payload: VariaveisCursorPayload): void {
        const editor = payload.editor;
        const data: VariaveisDisponiveisDialogData = {
            variaveis: this.variaveisUnicas,
            targetControl: payload.control,
            cursorStart: payload.selectionStart,
            cursorEnd: payload.selectionEnd,
            onInsert: editor ? (v: VariavelStruct) => editor.inserirVariavel(v) : undefined,
        };

        this.dialog.open(VariaveisDisponiveisDialogComponent, {
            width: '820px',
            maxWidth: '96vw',
            maxHeight: '90vh',
            data,
        });
    }
}
